import React from 'react';

import { SelectMenu } from '@reckon-web/select-menu';

import { NamedUserAvatar } from '../components/common/named-user-avatar';
import { Dialog } from '../components/common/dialog';
import { useJobSignOffRequest as _useJobSignOffRequest } from '../data/useJobSignOffRequest';
import { useGetMembersList } from '../data/useGetMembersList';
import { canApproveSignOff } from '../utils/user-security-utils';
import type { GetUserQueryItem } from '../data/useGetUser';

import { Job } from './use-query-job';
import { useDialog } from './use-dialog';

type JobSignOffRequestDialogResult = { signOffRequestedFromUserId: string };

const JobSignOffRequestDialog: React.FC<{
  defaultApprover: Job['partner'];
  onClose: (res?: JobSignOffRequestDialogResult) => void;
}> = ({ defaultApprover, onClose }) => {
  const [approver, setApprover] = React.useState<GetUserQueryItem>();

  const { loading, error, data: users } = useGetMembersList();

  // once we've loaded the users, set the selected based off the defaultApprover (if there is one)
  React.useEffect(() => {
    if (loading || !users || !defaultApprover) {
      return;
    }
    setApprover(
      users.find(
        (u) => u.cloudId === defaultApprover.cloudId && canApproveSignOff(u)
      )
    );
  }, [loading, users, defaultApprover]);

  // TODO
  if (loading) return <div>Loading...</div>;
  if (error || !users) return <div>Error!</div>;

  return (
    <Dialog
      title="Select approver to sign off the job"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Confirm approver"
      onSubmit={() =>
        onClose(
          approver?.cloudId
            ? { signOffRequestedFromUserId: approver.cloudId }
            : undefined
        )
      }
    >
      <SelectMenu<GetUserQueryItem>
        trigger={
          approver ? (
            <NamedUserAvatar
              user={approver}
              avatarSize="xsmall"
              textSize="small"
            />
          ) : (
            'Select approver'
          )
        }
        options={users.filter((user) => canApproveSignOff(user))}
        itemToLabel={(user) => user.fullName || ''}
        itemToValue={(user) => user.cloudId || ''}
        itemRenderer={(user) => (
          <NamedUserAvatar user={user} avatarSize="xsmall" textSize="small" />
        )}
        value={approver}
        onChange={setApprover}
      />
    </Dialog>
  );
};

export const useJobSignOffRequest = () => {
  const [_jobSignOffRequest] = _useJobSignOffRequest(); // TODO: handle loading and error states

  const [Dialog, openDialog] = useDialog();

  const jobSignOffRequest = React.useCallback(
    async (job: Job) => {
      const res = await openDialog<JobSignOffRequestDialogResult>(
        (props) => (
          <JobSignOffRequestDialog defaultApprover={job.partner} {...props} />
        ),
        { size: 'small' }
      );
      if (!res) return;

      return _jobSignOffRequest({
        variables: {
          cloudId: job.cloudId || '',
          signOffRequestedFromUserId: res.signOffRequestedFromUserId,
        },
      });
    },
    [_jobSignOffRequest, openDialog]
  );

  return React.useMemo(
    () =>
      [Dialog, jobSignOffRequest] as [typeof Dialog, typeof jobSignOffRequest],
    [Dialog, jobSignOffRequest]
  );
};
